"use client";

import { useFloating } from "@floating-ui/react-dom";
import * as Dialog from "@radix-ui/react-dialog";
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
} from "motion/react";
import { useTranslations } from "next-intl";
import {
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
  type ReactNode,
} from "react";
import { useDebounceValue, useWindowSize } from "usehooks-ts";

import { cn } from "@trip/utils";

import { AngleUpIcon } from "~/components/icons/angle-up";
import { CheckmarkFilledIcon } from "~/components/icons/checkmark-filled-icon";
import { LoadingSpinner } from "~/components/loading-spinner";
import { Portal } from "~/components/portal";
import { Viewport } from "~/components/sm";
import { Button, buttonVariants } from "~/components/ui/button";
import { toast } from "~/components/ui/use-toast";
import { useBoundedScroll } from "~/lib/use-bounded-scroll";
import { useCart } from "~/lib/use-cart";
import { useCurrencyFormatter } from "~/lib/use-currency";
import { Link, useRouter } from "~/navigation";
import { trpcReact } from "~/trpc/client";

import { TripContext } from "../_lib/trip-context";
import { CartSettings } from "./cart-settings";
import { type TripMutationParams } from "./passengers-and-dates";

function ActionButton({
  isLoading,
  className,
}: {
  className: string;
  isLoading: boolean;
}) {
  const t = useTranslations("TourDetails");
  const tTrip = useTranslations("TripDetails");
  const tCommon = useTranslations("Common");
  const trip = useContext(TripContext);
  if (!trip) {
    throw new Error("TripContext is not available");
  }
  const { cart } = useCart();
  const cartLine = cart?.cartLines
    .filter((cartLine) => cartLine.type === "trip")
    .find((cartLine) => cartLine.lineItem.datoTripId === trip.datoTrip.id);

  const datoTripActionMutation = trpcReact.cart.datoTripAction.useMutation();
  const cartTripActionMutation = trpcReact.cart.cartTripAction.useMutation();

  const isDatoTrip = trip.lineItem === null;
  const isCartTrip = trip.lineItem !== null;

  if (
    (cartLine && cartLine.lineItem.status === "PUBLISHED") ||
    datoTripActionMutation.isSuccess ||
    cartTripActionMutation.isSuccess
  ) {
    return (
      <Link
        href="/checkout"
        className={buttonVariants({
          variant: "outline",
          className: "rounded-full! w-full",
        })}
      >
        {tCommon("checkout")}
      </Link>
    );
  }

  return (
    <Button
      onClick={(event) => {
        event.preventDefault();
        if (isDatoTrip) {
          // For DatoTrip, use ADD_TO_CART action that automatically forks
          void datoTripActionMutation
            .mutateAsync({
              type: "ADD_TO_CART",
              datoTripId: trip.datoTrip.id,
            })
            .then(() =>
              toast({
                description: (
                  <div className="flex w-full items-center justify-center gap-2">
                    <CheckmarkFilledIcon className="text-green size-4" />
                    <div>{t("addedToBag")}</div>
                  </div>
                ),
              }),
            );
        } else if (isCartTrip && trip.lineItem) {
          // For CartTrip, use ADD_TO_CART action on existing trip line item
          void cartTripActionMutation
            .mutateAsync({
              type: "ADD_TO_CART",
              tripLineItemId: trip.lineItem.id,
            })
            .then(() =>
              toast({
                description: (
                  <div className="flex w-full items-center justify-center gap-2">
                    <CheckmarkFilledIcon className="text-green size-4" />
                    <div>{t("addedToBag")}</div>
                  </div>
                ),
              }),
            );
        }
      }}
      variant="sunset"
      disabled={
        isLoading ||
        datoTripActionMutation.isPending ||
        cartTripActionMutation.isPending
      }
      className={cn(className, "disabled:opacity-100")}
    >
      {isLoading ||
      datoTripActionMutation.isPending ||
      cartTripActionMutation.isPending ? (
        <LoadingSpinner className="text-white" />
      ) : (
        <>{tTrip("addToCart")}</>
      )}
    </Button>
  );
}

function Desktop({
  children,
  isLoading,
  passengerCount,
  total,
  settings,
}: {
  children: ReactNode;
  isLoading: boolean;
  passengerCount: number;
  settings: ReactNode;
  total: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const tTrip = useTranslations("TripDetails");

  // Debounce the window size to optimize performance during resize events
  const [windowSize] = useDebounceValue(useWindowSize(), 500);
  const section = useFloating({
    strategy: "fixed",
    elements: { reference: document.getElementById("add-to-cart-expanded") },
  });

  const [sectionRect, setSectionRect] = useState(
    section.elements.reference?.getBoundingClientRect(),
  );

  // Motion value to control the bottom position of the section for the sticking effect
  const bottom = useMotionValue(0);
  const height = 650;

  function tick(latest: number) {
    if (!sectionRect) {
      return;
    }
    if (latest >= sectionRect.bottom + windowSize.height) {
      bottom.set(0);
      return;
    }
    /*
        Adjusting the 'bottom' position of the 'add-to-cart' section based on the current scroll
        position:

        1. `latest + windowSize.height` calculates the position of the bottom edge of the
          viewport relative to the top of the document.

        2. If this position (bottom edge of the viewport) is above the combined value of
          `sectionRect.top + height` (which represents the bottom edge of our 'add-to-cart'
          section):
            - We're in a scenario where the 'add-to-cart' section is partially visible at the
              bottom of the viewport.

        3. In such a case, we need to calculate how much of the 'add-to-cart' section is still
            outside the viewport, which is given by the expression: `sectionRect.top + height -
            (latest + windowSize.height)`.
            - This tells us the amount by which our 'add-to-cart' section exceeds the bottom edge
              of the viewport.

        4. We multiply the above value by -1 to get the negative offset, effectively shifting the
          'add-to-cart' section upwards, giving the appearance of it sliding out from the bottom.

        5. If the bottom edge of the viewport is below or equal to the bottom edge of our
          'add-to-cart' section, it means the entire section is within the viewport. Hence, we
          set the 'bottom' value to 0, ensuring the section stays fixed at its current position.
      */
    const value =
      latest + windowSize.height < sectionRect.top + height
        ? -1 * (sectionRect.top + height - (latest + windowSize.height))
        : 0;
    bottom.set(value);
  }

  useEffect(() => {
    const value = section.refs.reference.current?.getBoundingClientRect();

    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
    setSectionRect(value);
    tick(window.scrollY);
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, windowSize]);

  const sectionTop = sectionRect?.top ?? 302;
  const sectionHeight = sectionRect?.height ?? 800;

  // Calculate the scroll threshold where transitions start: it's the point where the bottom of the
  // section reaches the bottom of the viewport
  const sectionScrollThreshold = useMemo(
    () => window.scrollY + sectionTop + sectionHeight - windowSize.height,
    [windowSize.height, sectionHeight, sectionTop],
  );

  // Create a motion value to store and retrieve the current scroll position
  const { scrollYBoundedProgress, scrollY } = useBoundedScroll([
    sectionScrollThreshold,
    sectionScrollThreshold + 180,
  ]);

  // Attach an event to the scrollY motion value, so every time it changes, we adjust the bottom
  // position of our section
  useMotionValueEvent(scrollY, "change", tick);

  const formatCurrency = useCurrencyFormatter();

  return (
    <div className="pointer-events-none fixed inset-0 z-40">
      <div className="relative mx-auto size-full max-w-6xl">
        <motion.div
          className="shadow-card-sm pointer-events-auto absolute right-6 mb-10 rounded-3xl bg-white"
          style={{
            bottom,
            width: useTransform(
              scrollYBoundedProgress,
              [0, 1],
              [368, expanded ? 368 : 320],
            ),
          }}
        >
          <motion.div
            className="relative overflow-hidden"
            style={{
              height: useTransform(
                scrollYBoundedProgress,
                [0, 1],
                [468, expanded ? 468 : 0],
              ),
              opacity: useTransform(
                scrollYBoundedProgress,
                [0, 0.75, 1],
                [1, 0, expanded ? 1 : 0],
              ),
            }}
          >
            <div className="flex h-full flex-col">
              <div className="p-5">{settings}</div>
              <div className="h-px bg-neutral-200" />
              <div className="relative grow">
                <div className="absolute inset-0">
                  <div className="bg-linear-to-t pointer-events-none absolute inset-x-0 bottom-0 h-10 from-white to-transparent" />
                  <div className="h-full overflow-y-auto p-5">{children}</div>
                </div>
              </div>
              <div className="h-px bg-neutral-200" />
            </div>
          </motion.div>
          <div className="flex items-stretch p-5">
            <motion.div
              className="relative shrink-0 overflow-hidden"
              style={{
                width: useTransform(scrollYBoundedProgress, [0, 1], [0, 98]),
              }}
            >
              <div className="absolute inset-y-0 w-20">
                <div className="flex h-10 flex-col justify-center gap-0.5 px-1 leading-none">
                  <div>{formatCurrency(total)}</div>
                  <div className="text-xs text-neutral-500">
                    {tTrip("forPassengerCount", { count: passengerCount })}
                  </div>
                </div>
              </div>
              <motion.div
                style={{
                  right: useTransform(
                    scrollYBoundedProgress,
                    [0, 0.75, 1],
                    [0, 0, -8],
                  ),
                  width: useTransform(
                    scrollYBoundedProgress,
                    [0, 0.75, 1],
                    [20, 20, 10],
                  ),
                }}
                className="bg-linear-to-r pointer-events-none absolute inset-y-0 from-transparent to-white"
              />
            </motion.div>
            <ActionButton isLoading={isLoading} className="grow" />
            <motion.div
              className="relative shrink-0 overflow-hidden"
              style={{
                width: useTransform(scrollYBoundedProgress, [0, 1], [0, 48]),
              }}
            >
              <Button
                variant="secondary"
                size="circle"
                onClick={() => {
                  setExpanded(() => !expanded);
                }}
                aria-label="Expand"
                className="absolute right-0"
              >
                <AngleUpIcon
                  className={cn("transition-all", expanded ? "rotate-180" : "")}
                />
              </Button>
              <motion.div
                style={{
                  left: useTransform(
                    scrollYBoundedProgress,
                    [0, 0.75, 1],
                    [0, 0, -8],
                  ),
                  width: useTransform(
                    scrollYBoundedProgress,
                    [0, 0.75, 1],
                    [20, 20, 10],
                  ),
                }}
                className="bg-linear-to-l pointer-events-none absolute inset-y-0 from-transparent to-white"
              />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function Mobile({
  children,
  isLoading,
  passengerCount,
  total,
  settings,
}: {
  children: ReactNode;
  isLoading: boolean;
  passengerCount: number;
  settings: ReactNode;
  total: number;
}) {
  const [open, setOpen] = useState(false);
  const formatCurrency = useCurrencyFormatter();
  const tTrip = useTranslations("TripDetails");
  const tCommon = useTranslations("Common");
  const tCart = useTranslations("Cart");
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
      }}
    >
      <div
        className={cn(
          "fixed inset-x-0 bottom-0 z-30 bg-white",
          open ? "top-0" : "border-t-[0.5px]",
        )}
      >
        <div className="flex h-full flex-col">
          <Dialog.Overlay className="grow overflow-y-auto">
            <Dialog.Content className="">
              <div className="p-5">{settings}</div>
              <div className="h-px bg-neutral-200" />
              <div className="grow p-5">
                <div className="h-full">{children}</div>
              </div>
            </Dialog.Content>
          </Dialog.Overlay>

          <div className="shadow-card-sm flex gap-4 p-4">
            <div className="flex h-10 flex-col justify-center gap-0.5 px-1 leading-none">
              <div>{formatCurrency(total)}</div>
              <div className="whitespace-nowrap text-xs text-neutral-500">
                {tTrip("forPassengerCount", { count: passengerCount })}
              </div>
            </div>
            <Dialog.Trigger asChild>
              <Button
                variant="secondary"
                className="w-24 rounded-full"
                aria-label="Expand"
              >
                {open ? tCommon("close") : tCart("edit")}
              </Button>
            </Dialog.Trigger>
            <ActionButton isLoading={isLoading} className="grow" />
          </div>
        </div>
      </div>
    </Dialog.Root>
  );
}

export function AddToCart({
  datoTripId,
  total,
  children,
  passengerCount,
}: {
  children: ReactNode;
  datoTripId: string;
  passengerCount: number;
  total: number;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const trip = useContext(TripContext);
  if (!trip) {
    throw new Error("TripContext is not available");
  }
  const { lineItem } = trip;

  // Distinguish between DatoTrip and CartTrip based on lineItem existence
  const isDatoTrip = lineItem === null;
  const isCartTrip = lineItem !== null;

  const datoTripActionMutation = trpcReact.cart.datoTripAction.useMutation({
    onSuccess: () => {
      startTransition(() => {
        router.refresh();
      });
    },
  });

  const cartTripActionMutation = trpcReact.cart.cartTripAction.useMutation({
    onSuccess: () => {
      startTransition(() => {
        router.refresh();
      });
    },
  });

  function onChangeParams(params: TripMutationParams) {
    switch (params.type) {
      case "UPDATE_PASSENGERS": {
        if (isDatoTrip) {
          // For DatoTrip, use UPDATE_PASSENGERS action that automatically forks
          void datoTripActionMutation.mutateAsync({
            type: "UPDATE_PASSENGERS",
            datoTripId,
            adults: params.passengers.adults,
            children: params.passengers.children,
          });
        } else if (isCartTrip) {
          // For CartTrip, use UPDATE_PASSENGERS action on existing trip line item
          void cartTripActionMutation.mutateAsync({
            type: "UPDATE_PASSENGERS",
            tripLineItemId: lineItem.id,
            adults: params.passengers.adults,
            children: params.passengers.children,
          });
        }
        break;
      }
      case "UPDATE_DATE": {
        if (isDatoTrip) {
          // For DatoTrip, use UPDATE_DATE action that automatically forks
          void datoTripActionMutation.mutateAsync({
            type: "UPDATE_DATE",
            datoTripId,
            date: params.calendarDate.toDate("UTC"),
          });
        } else if (isCartTrip) {
          // For CartTrip, use UPDATE_DATE action on existing trip line item
          void cartTripActionMutation.mutateAsync({
            type: "UPDATE_DATE",
            tripLineItemId: lineItem.id,
            date: params.calendarDate.toDate("UTC"),
          });
        }
        break;
      }
      default: {
        // oxlint-disable-next-line no-unused-expressions
        params satisfies never;
      }
    }
  }

  const isLoading =
    datoTripActionMutation.isPending ||
    cartTripActionMutation.isPending ||
    isPending;
  const settings = (
    <CartSettings isLoading={isLoading} onChangeParams={onChangeParams} />
  );

  return (
    <Portal>
      <Viewport
        breakpoint="md"
        desktop={
          <Desktop
            isLoading={isLoading}
            passengerCount={passengerCount}
            total={total}
            settings={settings}
          >
            {children}
          </Desktop>
        }
        mobile={
          <Mobile
            isLoading={isLoading}
            passengerCount={passengerCount}
            total={total}
            settings={settings}
          >
            {children}
          </Mobile>
        }
      />
    </Portal>
  );
}
